import { STATUS_DOT_CLASS, STATUS_LABEL, type SectionStatus } from './types';

export interface SectionNavItem {
  id: string;
  label: string;
  status: SectionStatus;
  disabled?: boolean;
}

interface NavProps {
  items: SectionNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
}

/** Wizard: pasos en columna, con número o ✓ y el estado en texto. */
export function SectionStepper({ items, activeId, onSelect }: NavProps) {
  return (
    <nav
      aria-label="Pasos de configuración"
      data-testid="section-stepper"
      className="flex flex-col gap-1"
    >
      {items.map((item, i) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            aria-current={active ? 'step' : undefined}
            disabled={item.disabled}
            onClick={() => onSelect(item.id)}
            className={`flex items-start gap-3 text-left px-3 py-2 rounded-md transition-colors ${
              active
                ? 'bg-white dark:bg-neutral-800 shadow-sm ring-1 ring-primary-200 dark:ring-primary-800'
                : 'hover:bg-white/70 dark:hover:bg-neutral-800/60'
            } ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <span
              aria-hidden
              className={`w-5 h-5 mt-0.5 shrink-0 rounded-full grid place-items-center text-caption font-bold ${
                item.status === 'complete'
                  ? 'bg-success-600 text-white dark:bg-success-500'
                  : item.status === 'partial'
                    ? 'bg-warning-600 text-white dark:bg-warning-500'
                    : 'border border-neutral-300 text-neutral-400 dark:border-neutral-600'
              }`}
            >
              {item.status === 'complete' ? '✓' : i + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-body-sm font-semibold text-neutral-800 dark:text-neutral-100">
                {item.label}
              </span>
              <span className="block text-caption text-neutral-500 dark:text-neutral-400">
                {STATUS_LABEL[item.status]}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** Edición: las mismas secciones como tabs, con el punto de estado. */
export function SectionTabs({ items, activeId, onSelect }: NavProps) {
  return (
    <div
      role="tablist"
      aria-label="Secciones del tenant"
      data-testid="section-tabs"
      className="flex gap-1 overflow-x-auto border-b border-neutral-200 dark:border-neutral-700"
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`section-tab-${item.id}`}
            onClick={() => onSelect(item.id)}
            className={`inline-flex items-center gap-2 px-3 py-2.5 text-body-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              active
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-100'
            }`}
          >
            <span
              aria-hidden
              className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT_CLASS[item.status]}`}
            />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
